import {
  Box,
  Button,
  CircularProgress,
  ListItem,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import React, { ReactNode } from "react";
import { useLocation, useNavigate } from "../../../../modules/common";

export type INavBarItem = {
  icon: ReactNode;
  path?: string;
  label: string;
  isButton?: boolean;
  isOpen?: boolean;
  isSelected?: boolean;
  disabled?: boolean;
  // Only used when isButton is set.
  loading?: boolean;
};

export type NavBarItemProps = ListItemButtonProps & INavBarItem;

export const NavBarItem = ({
  path,
  icon,
  label,
  isButton = false,
  isOpen = true,
  disabled = false,
  loading = false,
  onClick,
}: Readonly<NavBarItemProps>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const selected = location.pathname === path;

  const selectedClassName = isDark
    ? "bg-neutral-white-10"
    : "bg-teamApp-surface-accent";
  const selectedBorder = `1px solid ${
    isDark ? "transparent" : "var(--team-app-filter-active-bg)"
  }`;

  const selectedAndOpen = selected && isOpen;
  const selectedAndClosed = selected && isOpen === false;

  const listItemButtonSx = isOpen
    ? {
        justifyContent: "flex-start",
        paddingLeft: "1rem",
        marginLeft: "1.5rem",
      }
    : {
        justifyContent: "center",
        paddingLeft: 0,
        marginLeft: 0,
      };

  const handleNavClick: React.MouseEventHandler<HTMLDivElement> = (ev) => {
    ev.stopPropagation();
    if (disabled) {
      return;
    }
    if (onClick) {
      onClick(ev);
    } else if (path) {
      navigate(path, { id: path });
    }
  };

  const getIconStroke = () => {
    if (selected)
      return isDark ? "var(--team-app-stripe-mid)" : theme.palette.primary.main;
    return isDark ? "var(--navy-50)" : theme.palette.secondary.dark;
  };

  const getContent = () => {
    if (isButton && label !== "") {
      return (
        <Button
          fullWidth
          sx={{
            marginLeft: "1.5rem",
            marginRight: "1.5rem",
            width: "12rem",
            textTransform: "none",
            "& .MuiButton-startIcon svg": {
              width: "1.4rem",
              height: "1.4rem",
            },
          }}
          variant="contained"
          color="primary"
          startIcon={icon}
          loading={loading}
          loadingPosition="start"
        >
          {label}
        </Button>
      );
    }

    if (isButton) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding=".65rem"
          borderRadius=".9rem"
          className={selected ? "bg-sapphire-10" : undefined}
          bgcolor={selected ? undefined : theme.palette.primary.main}
          sx={{
            marginX: "auto",
            "& svg": {
              width: "1.4rem",
              height: "1.4rem",
              display: "block",
            },
          }}
        >
          {loading ? (
            <CircularProgress
              size="1.4rem"
              sx={{ color: theme.palette.primary.contrastText }}
            />
          ) : (
            icon
          )}
        </Box>
      );
    }

    return getNavLink();
  };

  const getNavLink = () => (
    <ListItemButton
      disableTouchRipple
      disableRipple
      disableGutters
      disabled={disabled}
      className={selectedAndOpen ? selectedClassName : ""}
      sx={{
        borderRadius: ".75rem",
        border: selectedAndOpen ? selectedBorder : "none",
        ...listItemButtonSx,
      }}
      onClick={handleNavClick}
    >
      <ListItemIcon
        sx={{
          justifyContent: "center",
          minWidth: isOpen ? "auto" : 0,
          marginRight: isOpen ? "1rem" : 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius=".9rem"
          padding={selectedAndClosed ? ".65rem" : 0}
          className={selectedAndClosed ? selectedClassName : ""}
          sx={{
            border: selectedAndClosed ? selectedBorder : "none",
            "& svg": {
              width: "1.4rem",
              height: "1.4rem",
              display: "block",
            },
            "& path": {
              stroke: getIconStroke(),
            },
          }}
        >
          {icon}
        </Box>
      </ListItemIcon>

      {isOpen && (
        <ListItemText disableTypography>
          <Typography variant={selected ? "navBarItemSelected" : "navBarItem"}>
            {label}
          </Typography>
        </ListItemText>
      )}
    </ListItemButton>
  );

  return (
    <ListItem
      disableGutters
      disablePadding
      sx={{
        borderRadius: ".75rem",
        backgroundColor: "transparent",
        alignContent: "center",
      }}
    >
      {getContent()}
    </ListItem>
  );
};
